import React, { useId } from "react";

/**
 * Growie, the iGROWth mascot.
 *
 * A sprout with a headband: the same character the design prototypes use,
 * ported to a component so the product can show it too. Drawn from plain SVG
 * primitives rather than shipped as an image — it stays sharp at any size,
 * costs nothing to load, and can change expression, which is the whole point
 * of having a mascot on a page that is telling someone good news.
 */

export type GrowiePose = "cheer" | "wave" | "idle" | "think" | "sleep" | "sad";

const BODY = { light: "#4FBF5B", dark: "#2E9E42" };
const FOLIAGE = { light: "#5FD96C", dark: "#2E9E42" };
const BAND = "#FFFFFF";

type Props = {
  pose?: GrowiePose;
  className?: string;
  /** Screen-reader text. Omitted (decorative) unless given. */
  title?: string;
};

export default function Growie({ pose = "cheer", className, title }: Props) {
  // One id per instance: two Growies on a page must not share gradients.
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");

  const happy = pose === "cheer" || pose === "wave";
  const asleep = pose === "sleep";
  const sad = pose === "sad";
  const wonder = pose === "think";

  const eyes = asleep ? (
    <>
      <path d="M72 112q10 9 20 0" stroke="#1E1E1E" strokeWidth="4.5" fill="none" strokeLinecap="round" />
      <path d="M108 112q10 9 20 0" stroke="#1E1E1E" strokeWidth="4.5" fill="none" strokeLinecap="round" />
    </>
  ) : happy ? (
    <>
      <path d="M72 116q10-13 20 0" stroke="#1E1E1E" strokeWidth="5.5" fill="none" strokeLinecap="round" />
      <path d="M108 116q10-13 20 0" stroke="#1E1E1E" strokeWidth="5.5" fill="none" strokeLinecap="round" />
    </>
  ) : (
    <>
      <ellipse cx="82" cy="112" rx="10.5" ry="12.5" fill="#1E1E1E" />
      <ellipse cx="118" cy="112" rx="10.5" ry="12.5" fill="#1E1E1E" />
      <circle cx="78.5" cy="107" r="3.6" fill="#fff" />
      <circle cx="114.5" cy="107" r="3.6" fill="#fff" />
      <circle cx="86" cy="117" r="1.8" fill="#fff" opacity=".8" />
      <circle cx="122" cy="117" r="1.8" fill="#fff" opacity=".8" />
    </>
  );

  const brows = sad ? (
    <>
      <path d="M70 95q10-5 20-1" stroke="#1E1E1E" strokeWidth="4" strokeLinecap="round" fill="none" />
      <path d="M130 95q-10-5-20-1" stroke="#1E1E1E" strokeWidth="4" strokeLinecap="round" fill="none" />
    </>
  ) : wonder ? (
    <path d="M70 92q10-6 20 0" stroke="#1E1E1E" strokeWidth="4" strokeLinecap="round" fill="none" />
  ) : (
    <>
      <path d="M71 94q11-7 22-1" stroke="#1E1E1E" strokeWidth="4.2" strokeLinecap="round" fill="none" />
      <path d="M129 94q-11-7-22-1" stroke="#1E1E1E" strokeWidth="4.2" strokeLinecap="round" fill="none" />
    </>
  );

  const mouth = sad ? (
    <path d="M88 140q12-9 24 0" stroke="#B4453C" strokeWidth="4.5" fill="none" strokeLinecap="round" />
  ) : asleep ? (
    <ellipse cx="100" cy="138" rx="6" ry="7" fill="#C1392F" />
  ) : (
    <>
      <path d="M80 130q20 26 40 0 z" fill="#C1392F" />
      <path d="M89 145q11 9 22 0 z" fill="#F0868B" />
    </>
  );

  const arms = happy ? (
    <>
      <ellipse cx="34" cy="105" rx="13" ry="19" fill={BODY.dark} transform="rotate(-28 34 105)" />
      <ellipse cx="166" cy="105" rx="13" ry="19" fill={BODY.dark} transform="rotate(28 166 105)" />
    </>
  ) : wonder ? (
    <>
      <ellipse cx="38" cy="140" rx="13" ry="18" fill={BODY.dark} transform="rotate(14 38 140)" />
      <ellipse cx="158" cy="112" rx="12" ry="18" fill={BODY.dark} transform="rotate(-42 158 112)" />
    </>
  ) : (
    <>
      <ellipse cx="36" cy="136" rx="13" ry="19" fill={BODY.dark} transform="rotate(16 36 136)" />
      <ellipse cx="164" cy="136" rx="13" ry="19" fill={BODY.dark} transform="rotate(-16 164 136)" />
    </>
  );

  return (
    <svg
      viewBox="0 0 200 210"
      className={className}
      role={title ? "img" : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      <defs>
        <radialGradient id={`growie-body-${uid}`} cx=".36" cy=".3" r=".85">
          <stop offset="0" stopColor={BODY.light} />
          <stop offset="1" stopColor={BODY.dark} />
        </radialGradient>
        <linearGradient id={`growie-leaf-${uid}`} x1="0" y1="0" x2=".6" y2="1">
          <stop offset="0" stopColor={FOLIAGE.light} />
          <stop offset="1" stopColor={FOLIAGE.dark} />
        </linearGradient>
      </defs>

      <ellipse cx="100" cy="196" rx="46" ry="8" fill="#14301F" opacity=".12" />

      {/* stem and leaves */}
      <path d="M98 46q2-14 4-20" stroke={FOLIAGE.dark} strokeWidth="6" strokeLinecap="round" fill="none" />
      <path d="M100 30q-26-16-34 2 20 14 34-2z" fill={`url(#growie-leaf-${uid})`} />
      <path d="M100 30q22-20 34-2-16 16-34 2z" fill={`url(#growie-leaf-${uid})`} />
      <path d="M74 32q12 0 24 0" stroke={FOLIAGE.dark} strokeWidth="2" opacity=".45" fill="none" />

      {/* feet */}
      <ellipse cx="80" cy="186" rx="15" ry="11" fill={BODY.dark} />
      <ellipse cx="120" cy="186" rx="15" ry="11" fill={BODY.dark} />

      {arms}

      {/* body */}
      <ellipse cx="100" cy="118" rx="72" ry="70" fill={`url(#growie-body-${uid})`} />
      <ellipse cx="100" cy="128" rx="52" ry="47" fill="#FBEBCD" />

      {/* cheeks */}
      <ellipse cx="64" cy="130" rx="9.5" ry="6.5" fill="#F7A9A2" opacity=".8" />
      <ellipse cx="136" cy="130" rx="9.5" ry="6.5" fill="#F7A9A2" opacity=".8" />

      {brows}
      {eyes}
      {mouth}

      {/* headband */}
      <path d="M57 92q43-22 86 0l-2 11q-42-20-82 0z" fill={BAND} />
      <circle cx="100" cy="87" r="5" fill={FOLIAGE.dark} />
    </svg>
  );
}
